import { Injectable, Injector, inject } from '@angular/core';
import { TokenService } from '@services/token/token.service';
import { SystemService } from '@services/system/system.service';
import {
  GrpcStatusCode,
  UnauthenticatedError,
  getPermissionDeniedMessage,
  isPermissionDeniedError,
} from '@utils/backend-error.utils';
import { getRpcMethodName, requiresAuthentication } from '@utils/grpc-public-rpc.utils';
import { logClientError } from '@utils/server-log';
import { invokeWithBackendLog } from './backend-log.interceptor';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationInterceptor {
  private readonly tokenService = inject(TokenService);
  private readonly injector = inject(Injector);
  private readonly maxRetries = 3;
  private lastPermissionDeniedToastAt = 0;
  private static readonly PERMISSION_TOAST_COOLDOWN_MS = 2500;

  intercept = async (request: any, invoker: any) => {
    const token = await this.tokenService.ensureValidToken();

    if (token) {
      request.getMetadata()['Authorization'] = `Bearer ${token}`;
    } else if (requiresAuthentication(request)) {
      // Sem credencial utilizável a request não pode sair. Sem o header o
      // backend não resolve o usuário e responde NOT_FOUND ("usuário não
      // encontrado"), mascarando uma sessão expirada como falha de serviço.
      const method = getRpcMethodName(request);
      logClientError(`[Interceptor] ${method} abortada: sem credencial válida.`);
      throw new UnauthenticatedError(
        `Sessão expirada: ${method} exige autenticação e não há token válido.`
      );
    }

    return this.handleRequestWithRetry(request, invoker, 1);
  };

  private async handleRequestWithRetry(
    request: any,
    invoker: any,
    attempt: number
  ): Promise<any> {
    try {
      return await invokeWithBackendLog(request, invoker, attempt);
    } catch (error: any) {
      if (
        error?.code === GrpcStatusCode.UNAUTHENTICATED &&
        attempt <= this.maxRetries &&
        // Refresh token já rejeitado: repetir só gera `invalid_grant` em série.
        !this.tokenService.isRefreshRejected
      ) {

        // `force`: o servidor rejeitou a credencial, então renovar é preciso
        // mesmo que o token pareça válido pelo `exp` local.
        const refreshed = await this.tokenService.refreshAccessToken(true);
        if (!refreshed) {
          return Promise.reject(error);
        }

        const newToken = this.tokenService.getAccessToken();
        if (newToken) {
          request.getMetadata()['Authorization'] = `Bearer ${newToken}`;
        }

        return this.handleRequestWithRetry(request, invoker, attempt + 1);
      }

      if (isPermissionDeniedError(error)) {
        this.notifyPermissionDenied(error);
      }

      throw error;
    }
  }

  private notifyPermissionDenied(error: unknown): void {
    const now = Date.now();
    if (
      now - this.lastPermissionDeniedToastAt <
      AuthorizationInterceptor.PERMISSION_TOAST_COOLDOWN_MS
    ) {
      return;
    }
    this.lastPermissionDeniedToastAt = now;

    try {
      const system = this.injector.get(SystemService);
      system.showErrorToast(getPermissionDeniedMessage(error), 'Sem permissão');
    } catch {
      logClientError('[Interceptor] Permission denied:', getPermissionDeniedMessage(error));
    }
  }
}
